// Permission utilities for checking user access

// Map route paths to permission IDs
export const ROUTE_PERMISSION_MAP = {
  '/': 'dashboard',
  '/dashboard': 'dashboard',
  '/inventory': 'calendar',
  '/calendar': 'calendar',
  '/bookings': 'bookings',
  '/walkin-rates': 'walkin_rates',
  '/rates': 'rates',
  '/pos': 'pos',
  '/pos/misc-charges': 'misc_charges',
  '/elda-pos-report': 'elda_pos_report',
  '/booking-engine': 'booking_engine',
  '/booking-engine/settings': 'booking_engine',
  '/properties': 'properties',
  '/reconciliation': 'reconciliation',
  '/security-deposits': 'security_deposits',
  '/invoices': 'invoices',
  '/invoice-report': 'invoice_report',
  '/daily-sales-report': 'daily_sales_report',
  '/reports': 'reports',
  '/rate-sync-logs': 'rate_sync_logs',
  '/inbox': 'inbox',
  '/reviews': 'reviews',
  '/users': 'users',
  '/user-logs': 'user_logs',
  '/scheduled-reports': 'scheduled_reports',
  '/settings': 'settings',
  '/profile': null, // Always accessible
  '/contact-us': null, // Always accessible (public contact info)
  '/counter-rates': 'counter_rates',
  '/omnichat': 'omnichat',
  '/email-settings': 'email_settings',
  '/admin/gmail': 'gmail',
  '/guest-forms/checkins': 'guest_checkin_forms',
  '/guest-forms/checkouts': 'guest_checkout_forms',
  '/einvoice': 'einvoice_dashboard',
  '/einvoice/dashboard': 'einvoice_dashboard',
  '/einvoice/invoices': 'einvoice_invoices',
  '/einvoice/integrations': 'einvoice_integrations',
  '/einvoice/reports': 'einvoice_reports',
  '/einvoice/portal/settings': 'einvoice_user_access',
};

// All available pages for permission configuration
export const ALL_PAGES = [
  { id: 'dashboard', name: 'Dashboard', category: 'Core' },
  { id: 'calendar', name: 'Smart Calendar', category: 'Operations' },
  { id: 'bookings', name: 'Reservations', category: 'Operations' },
  { id: 'walkin_rates', name: 'Digital Channel Rate', category: 'Operations' },
  { id: 'counter_rates', name: 'Walk In Rate', category: 'Operations' },
  { id: 'rates', name: 'Rates & Availability', category: 'Revenue' },
  { id: 'pos', name: 'Elda POS Terminal', category: 'Revenue' },
  { id: 'misc_charges', name: 'Misc Charges', category: 'Revenue' },
  { id: 'elda_pos_report', name: 'Elda POS Report', category: 'Revenue' },
  { id: 'booking_engine', name: 'Booking Engine', category: 'Revenue' },
  { id: 'properties', name: 'Properties', category: 'Property' },
  { id: 'reconciliation', name: 'OTA Reconciliation', category: 'Finance' },
  { id: 'security_deposits', name: 'Security Deposits', category: 'Finance' },
  { id: 'invoices', name: 'Compliance Hub', category: 'Finance' },
  { id: 'invoice_report', name: 'Invoice Center', category: 'Finance' },
  { id: 'einvoice_dashboard', name: 'e-Invoice Dashboard', category: 'E-Invoice' },
  { id: 'einvoice_invoices', name: 'e-Invoice Invoices', category: 'E-Invoice' },
  { id: 'einvoice_integrations', name: 'e-Invoice Integrations', category: 'E-Invoice' },
  { id: 'einvoice_reports', name: 'e-Invoice Reports', category: 'E-Invoice' },
  { id: 'einvoice_user_access', name: 'e-Invoice User Access', category: 'E-Invoice' },
  { id: 'daily_sales_report', name: 'Daily Sales Report', category: 'Analytics' },
  { id: 'reports', name: 'Reports', category: 'Analytics' },
  { id: 'rate_sync_logs', name: 'Rate Sync Logs', category: 'Analytics' },
  { id: 'inbox', name: 'Inbox', category: 'Guest' },
  { id: 'reviews', name: 'Reviews', category: 'Guest' },
  { id: 'guest_checkin_forms', name: 'Check-In Forms', category: 'Guest Registration' },
  { id: 'guest_checkout_forms', name: 'Check-Out Forms', category: 'Guest Registration' },
  { id: 'users', name: 'Access Control', category: 'Admin' },
  { id: 'user_logs', name: 'Audit Trail', category: 'Admin' },
  { id: 'omnichat', name: 'StayMii OmniChat', category: 'Admin' },
  { id: 'email_settings', name: 'Email Automation', category: 'Admin' },
  { id: 'gmail', name: 'Gmail', category: 'Admin' },
  { id: 'settings', name: 'Settings', category: 'Admin' },
  { id: 'scheduled_reports', name: 'Scheduled Reports', category: 'Admin' },
];

// Roles that have full system access regardless of granular permission grants.
// These are the privileged roles that should never be locked out by per-page
// view/edit/delete checks.
const ADMIN_ROLES = ['admin', 'super_admin', 'owner'];

const isAdminRole = (user) => {
  const r = (user?.role || '').toLowerCase().replace(/\s+/g, '_');
  return ADMIN_ROLES.includes(r);
};

// Check if user has view permission for a specific page
export const hasViewPermission = (user, pageId) => {
  // Profile page is always accessible
  if (pageId === null) return true;

  // Check user permissions
  const permissions = user?.permissions;

  // Privileged roles (admin / super_admin / owner) with no explicit permissions
  // → grant full access (backward compat)
  if (isAdminRole(user) && (!permissions || Object.keys(permissions).length === 0)) return true;

  // If permissions exist, respect them for all roles including admin
  if (permissions && permissions[pageId] !== undefined) {
    return permissions[pageId]?.view === true;
  }

  // Privileged role with permissions set but page not configured —
  // grant access to new pages (avoids locking out super admin from any
  // newly-added feature that hasn't been added to their permissions map yet)
  if (isAdminRole(user)) return true;

  // Non-admin: page not in user's config — deny access (unconfigured = no access)
  return false;
};

// Check if user has edit permission for a specific page
export const hasEditPermission = (user, pageId) => {
  const permissions = user?.permissions;

  if (isAdminRole(user) && (!permissions || Object.keys(permissions).length === 0)) return true;

  if (permissions && permissions[pageId] !== undefined) {
    return permissions[pageId]?.edit === true;
  }

  if (isAdminRole(user)) return true;
  return false;
};

// Check if user has delete permission for a specific page
export const hasDeletePermission = (user, pageId) => {
  const permissions = user?.permissions;

  if (isAdminRole(user) && (!permissions || Object.keys(permissions).length === 0)) return true;

  if (permissions && permissions[pageId] !== undefined) {
    return permissions[pageId]?.delete === true;
  }

  if (isAdminRole(user)) return true;
  return false;
};

// Check if user can access a specific route path
export const canAccessRoute = (user, path) => {
  // StayMii Lite unit owners are restricted to their own scoped pages.
  if (user?.lite_role === 'unit_owner') {
    return ['/inventory', '/bookings', '/owner-reports', '/profile'].includes(path);
  }
  const pageId = ROUTE_PERMISSION_MAP[path];
  
  // Unknown routes - allow access (will show 404 if needed)
  if (pageId === undefined) return true;
  
  return hasViewPermission(user, pageId);
};

// Get the first accessible route for a user (for redirects)
export const getFirstAccessibleRoute = (user) => {
  // Unit owners land on their Smart Calendar, never the all-units Dashboard.
  if (user?.lite_role === 'unit_owner') return '/inventory';
  const routes = ['/', '/inventory', '/bookings', '/rates', '/pos', '/properties', '/reports'];
  
  for (const route of routes) {
    if (canAccessRoute(user, route)) {
      return route;
    }
  }
  
  // Fallback to profile page which is always accessible
  return '/profile';
};

// Filter menu items based on user permissions
export const filterMenuByPermissions = (menuGroups, user) => {
  const permissions = user?.permissions;

  // Privileged roles (admin / super_admin / owner) with no explicit
  // permissions set — show full menu
  if (isAdminRole(user) && (!permissions || Object.keys(permissions).length === 0)) return menuGroups;

  return menuGroups
    .map(group => ({
      ...group,
      items: group.items.filter(item => {
        const pageId = ROUTE_PERMISSION_MAP[item.path];
        return hasViewPermission(user, pageId);
      })
    }))
    .filter(group => group.items.length > 0);
};
